import { useState } from "react";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import { adminApi, apiError } from "@/lib/api";
import { Panel, Labeled, inputCls, SaveButton } from "./AdminUI";

const ServiceEditor = ({ svc, onSaved }) => {
  const [s, setS] = useState(svc);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setS({ ...s, [k]: v });
  const tier = (i, k, v) => set("size_tiers", s.size_tiers.map((t, j) => (j === i ? { ...t, [k]: Number(v) } : t)));
  const blocked = s.requires_eligibility && !s.eligibility_confirmed;

  const save = async () => {
    setSaving(true);
    try {
      const { id, sort, requires_eligibility, kind, category, tags, ...body } = s;
      const r = await adminApi.put(`/admin/inspections/services/${s.id}`, body);
      setS(r.data);
      onSaved(r.data);
      toast.success(`${r.data.name} saved`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200" data-testid={`svc-editor-${s.id}`}>
      <div className="flex flex-wrap items-center gap-3 p-4">
        <span className={`h-2.5 w-2.5 rounded-full ${s.active ? "bg-emerald-500" : "bg-slate-300"}`} />
        <button type="button" className="flex-1 text-left font-semibold text-[#0B132B]" onClick={() => setOpen(!open)} data-testid={`svc-toggle-${s.id}`}>{s.name}</button>
        {s.requires_eligibility && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700"><ShieldAlert className="h-3 w-3" /> Eligibility required</span>}
        <div className="flex items-center gap-1 text-sm">$<input type="number" min="0" placeholder="Quote" className={`${inputCls} w-24`} value={s.base_price ?? ""} onChange={(e) => set("base_price", e.target.value === "" ? null : Number(e.target.value))} data-testid={`svc-price-${s.id}`} /></div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={s.active} disabled={blocked && !s.active} onChange={(e) => set("active", e.target.checked)} data-testid={`svc-active-${s.id}`} /> Active
        </label>
        <SaveButton onClick={save} saving={saving} testid={`svc-save-${s.id}`} />
      </div>
      {s.requires_eligibility && (
        <label className="mx-4 mb-3 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
          <input type="checkbox" checked={s.eligibility_confirmed} onChange={(e) => setS({ ...s, eligibility_confirmed: e.target.checked, active: e.target.checked && s.active })} data-testid={`svc-eligibility-${s.id}`} />
          I confirm we meet all licensing, certification, insurance and other legal requirements to offer this service in Pennsylvania.
        </label>
      )}
      {open && (
        <div className="grid gap-4 border-t border-slate-100 p-4 sm:grid-cols-2">
          <Labeled label="Name"><input className={inputCls} value={s.name} onChange={(e) => set("name", e.target.value)} /></Labeled>
          <Labeled label="Duration (minutes)"><input type="number" className={inputCls} value={s.duration_minutes} onChange={(e) => set("duration_minutes", Number(e.target.value))} data-testid={`svc-duration-${s.id}`} /></Labeled>
          <Labeled label="Description" className="sm:col-span-2"><textarea rows={3} className={inputCls} value={s.description} onChange={(e) => set("description", e.target.value)} /></Labeled>
          <Labeled label="What's included (one per line)" className="sm:col-span-2">
            <textarea rows={4} className={inputCls} value={s.includes.join("\n")} onChange={(e) => set("includes", e.target.value.split("\n"))} />
          </Labeled>
          <Labeled label="Image URL" className="sm:col-span-2"><input className={inputCls} value={s.image} onChange={(e) => set("image", e.target.value)} /></Labeled>
          {s.kind === "quote" && (
            <div className="grid gap-3 sm:col-span-2 sm:grid-cols-4">
              {[["mileage_rate", "Mileage ($/mile)"], ["rush_surcharge", "Rush surcharge"], ["interior_surcharge", "Interior access surcharge"], ["per_property_charge", "Per extra property/unit"]].map(([k, l]) => (
                <Labeled key={k} label={l}><input type="number" min="0" className={inputCls} value={s[k] ?? 0} onChange={(e) => set(k, Number(e.target.value))} data-testid={`svc-${k}-${s.id}`} /></Labeled>
              ))}
              <label className="flex items-center gap-2 text-sm sm:col-span-4"><input type="checkbox" checked={!!s.custom_quote} onChange={(e) => set("custom_quote", e.target.checked)} /> Final price confirmed by custom quote</label>
              <p className="text-xs text-slate-400 sm:col-span-4">Leave the price empty to show "Request a Quote". The site shows "Starting at" only when a price is set.</p>
            </div>
          )}
          {s.kind !== "quote" && <div className="sm:col-span-2">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Size adjustments</p>
            {s.size_tiers.map((t, i) => (
              <div key={i} className="mb-2 flex items-center gap-2 text-sm">
                Up to <input type="number" className={`${inputCls} w-28`} value={t.max_sqft} onChange={(e) => tier(i, "max_sqft", e.target.value)} data-testid={`svc-tier-max-${s.id}-${i}`} /> sq ft: +$
                <input type="number" className={`${inputCls} w-24`} value={t.adjustment} onChange={(e) => tier(i, "adjustment", e.target.value)} data-testid={`svc-tier-adj-${s.id}-${i}`} />
                <button type="button" className="text-xs text-red-500" onClick={() => set("size_tiers", s.size_tiers.filter((_, j) => j !== i))}>Remove</button>
              </div>
            ))}
            <button type="button" className="text-sm font-semibold text-gold" onClick={() => set("size_tiers", [...s.size_tiers, { max_sqft: 5000, adjustment: 0 }])}>+ Add tier</button>
            <div className="mt-3 flex items-center gap-2 text-sm">Custom quote above <input type="number" className={`${inputCls} w-28`} value={s.custom_quote_over} onChange={(e) => set("custom_quote_over", Number(e.target.value))} /> sq ft (0 = never)</div>
          </div>}
        </div>
      )}
    </div>
  );
};

export const ServicesPanel = ({ services, onSaved }) => (
  <Panel title="Pricing & services">
    <p className="mb-4 text-sm text-slate-500">Edit starting prices, durations, size adjustments and publishing status. Services marked "Eligibility required" can only be activated after you confirm legal eligibility.</p>
    <div className="space-y-3">{services.map((s) => <ServiceEditor key={s.id} svc={s} onSaved={onSaved} />)}</div>
  </Panel>
);
